import { Grid, InputAdornment, Typography } from '@mui/material'
import React, { Dispatch, Fragment, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ChargeException } from '../../../../api/models/ChargeException.type'
import { FeeType } from '../../../../api/models/Common/JyrneyEnums'
import {
  ChargeException as ChargeRateException,
  ChargeRate,
} from '../../../../api/vehicle/taxisAPI.types'
import { useSupplierState } from '../../../../Pages/Supplier/SupplierContext'
import CurrencyInput, {
  calcValue,
} from '../../../FormFields/Currency/CurrencyInput'
import FormInput from '../../../FormFields/FormInput/FormInput'
import { Select } from '../../../FormFields/Select/Select'
import styles from '../manageSupplierStyle'

export interface ChargeOverride {
  description: string
  enabled: boolean
  type: string
  percentage?: number
  chargeRates: Array<ChargeRate>
  setChargeRates: Dispatch<React.SetStateAction<Array<ChargeRate>>>
}

const ExceptionRow = (props: {
  chargeException: ChargeException
  chargeExceptionIndex: number
  waitingTime: string
  chargeRates: Array<ChargeRate>
  setChargeRates: Dispatch<React.SetStateAction<Array<ChargeRate>>>
  isEdit: boolean
  additionalStop: boolean
  waitAndReturn: boolean
  displayOrder?: number[]
}) => {
  const {
    chargeException,
    chargeExceptionIndex,
    waitingTime,
    chargeRates,
    setChargeRates,
    isEdit,
    additionalStop,
    waitAndReturn,
    displayOrder,
  } = props
  const order = displayOrder ?? chargeRates.map((_, idx) => idx)
  const formMethods = useFormContext()
  const [type, setType] = useState('')
  const [chargeRateException, setChargeRateException] =
    useState<ChargeRateException>()
  const [percentage, setPercentage] = useState<number | undefined>()

  const handleTypeChangeEvent = (event: React.BaseSyntheticEvent) => {
    const value = event.target.value
    setType(value)
    let newRate
    if (value === 'Percentage') {
      const mappedRates: Array<ChargeRate> = chargeRates.map((rate, i) => {
        const pricePerMile = +formMethods.getValues(
          `chargeRates.${i}.pricePerMile`,
        )
        const found = rate.chargeExceptions.findIndex(
          (ec) => ec.chargeExceptionId === chargeException.id,
        )
        newRate = {
          chargeExceptionId: chargeException?.id || 0,
          chargeTypeName: 'Percentage',
          value: +(event.target.value / 100) * pricePerMile + pricePerMile,
        }
        if (found >= 0) {
          rate.chargeExceptions[found] = newRate
        } else {
          rate.chargeExceptions.push({
            chargeExceptionId: chargeException?.id || 0,
            chargeTypeName: 'Percentage',
            value: +(event.target.value / 100) * pricePerMile + pricePerMile,
          })
        }
        return rate
      })
      formMethods.setValue(
        `chargeExceptions.${chargeExceptionIndex}.chargeExceptionPremiumTypeName`,
        value,
      )
      setChargeRateException(newRate)
      setChargeRates([...mappedRates])
    }
    if (value && value !== '') {
      formMethods.setValue(
        `chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
        chargeException.id,
      )
    }
  }

  const handlePercentageChangeEvent = (event: React.BaseSyntheticEvent) => {
    let newRate
    setPercentage(event.target.value)
    const mappedRates: Array<ChargeRate> = chargeRates.map((rate, i) => {
      const pricePerMile = +formMethods.getValues(
        `chargeRates.${i}.pricePerMile`,
      )
      const found = rate.chargeExceptions.findIndex(
        (ec) => ec.chargeExceptionId === chargeException.id,
      )
      newRate = {
        chargeExceptionId: chargeException?.id || 0,
        chargeTypeName: 'Percentage',
        value: (+event.target.value / 100) * +pricePerMile + +pricePerMile,
      }
      if (found >= 0) {
        rate.chargeExceptions[found] = newRate
      } else {
        rate.chargeExceptions.push({
          chargeExceptionId: chargeException?.id || 0,
          chargeTypeName: 'Percentage',
          value: (+event.target.value / 100) * +pricePerMile + +pricePerMile,
        })
      }
      return rate
    })
    setChargeRateException(newRate)
    setChargeRates([...mappedRates])
  }

  useEffect(() => {
    if (!isEdit && chargeRates && chargeRates.length > 0) {
      const found = chargeRates[0].chargeExceptions.find(
        (ec) => ec.chargeExceptionId === chargeException.id,
      )
      if (found && found?.chargeTypeName) {
        setType(found?.chargeTypeName)
        setChargeRateException(found)
        if (found.chargeTypeName === 'Percentage') {
          setPercentage(found.value)
        }
      }
    }
  }, [chargeRates])

  useEffect(() => {
    if (type === 'Percentage') {
      formMethods.setValue(
        `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
        type,
      )
      formMethods.setValue(
        `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
        chargeException.id,
      )
      formMethods.setValue(
        `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.value`,
        percentage,
      )
      formMethods.setValue(
        `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
        type,
      )
      formMethods.setValue(
        `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
        chargeException.id,
      )
      formMethods.setValue(
        `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`,
        percentage,
      )
      if (additionalStop) {
        formMethods.setValue(
          `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.value`,
          percentage,
        )
        formMethods.setValue(
          `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
          type,
        )
        formMethods.setValue(
          `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
          chargeException.id,
        )
      }
      if (waitAndReturn) {
        formMethods.setValue(
          `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`,
          percentage,
        )
        formMethods.setValue(
          `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
          type,
        )
        formMethods.setValue(
          `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
          chargeException.id,
        )
      }
      if (waitingTime === 'grace') {
        formMethods.setValue(
          `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.value`,
          percentage,
        )
        formMethods.setValue(
          `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
          type,
        )
        formMethods.setValue(
          `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
          chargeException.id,
        )
      }
    }
  }, [percentage])

  return (
    <Grid
      container
      key={`chargeException.index_${chargeExceptionIndex}.exception_${chargeException.id}`}
      spacing={2}
      direction="row"
      alignItems="center"
    >
      <Grid item xs={1}>
        <Typography>{chargeException.name}</Typography>
      </Grid>
      <Grid item xs={1}>
        <Select
          name={`chargeExceptions.${chargeExceptionIndex}.chargeExceptionPremiumTypeName`}
          label="Fee type"
          options={FeeType}
          value={type}
          disabled={!isEdit}
          onChange={handleTypeChangeEvent}
        />
      </Grid>
      {type === 'Percentage' && (
        <Grid item xs={1}>
          <FormInput
            name={`chargeExceptions.${chargeExceptionIndex}.value`}
            sx={styles.inputField}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            type="tel"
            defaultValue={
              chargeRateException?.value ? chargeRateException?.value : ''
            }
            onChange={(event) => handlePercentageChangeEvent(event)}
            disabled={!isEdit}
          />
        </Grid>
      )}
      {type !== 'Percentage' && <Grid item xs={1} />}
      {type && (
        <Grid item xs={1}>
          <CurrencyInput
            label=""
            name={`vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.value`}
            defaultValue={
              type === 'Percentage'
                ? calcValue(
                    formMethods.getValues('vehicle.minimumFare.price'),
                    percentage,
                  )
                : calcValue(
                    formMethods.getValues(
                      `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.value`,
                    ),
                  )
            }
            sx={styles.inputField}
            disabled={!isEdit || type === 'Percentage'}
            onChange={() => {
              formMethods.setValue(
                `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                chargeException?.id,
              )
              formMethods.setValue(
                `vehicle.minimumFare.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                type,
              )
            }}
          />
        </Grid>
      )}
      {type && waitingTime === 'grace' && (
        <Grid item xs={1}>
          <CurrencyInput
            label=""
            name={`vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.value`}
            defaultValue={
              type === 'Percentage'
                ? calcValue(
                    formMethods.getValues(
                      'vehicle.waitingTime.gracePeriod.price',
                    ),
                    percentage,
                  )
                : calcValue(
                    formMethods.getValues(
                      `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.value`,
                    ),
                  )
            }
            sx={styles.inputField}
            disabled={!isEdit || type === 'Percentage'}
            onChange={() => {
              formMethods.setValue(
                `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                chargeException?.id,
              )
              formMethods.setValue(
                `vehicle.waitingTime.gracePeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                type,
              )
            }}
          />
        </Grid>
      )}
      {type && waitingTime !== '' && (
        <Grid item xs={1}>
          <CurrencyInput
            label=""
            name={`vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`}
            defaultValue={
              type === 'Percentage'
                ? calcValue(
                    formMethods.getValues(
                      'vehicle.waitingTime.chargingPeriod.price',
                    ),
                    percentage,
                  )
                : calcValue(
                    formMethods.getValues(
                      `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`,
                    ),
                  )
            }
            sx={styles.inputField}
            disabled={!isEdit || type === 'Percentage'}
            onChange={() => {
              formMethods.setValue(
                `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                chargeException?.id,
              )
              formMethods.setValue(
                `vehicle.waitingTime.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                type,
              )
            }}
          />
        </Grid>
      )}
      {type && waitAndReturn && (
        <Grid item xs={1}>
          <CurrencyInput
            label=""
            name={`vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`}
            defaultValue={
              type === 'Percentage'
                ? calcValue(
                    formMethods.getValues(
                      'vehicle.waitAndReturn.chargingPeriod.price',
                    ),
                    percentage,
                  )
                : calcValue(
                    formMethods.getValues(
                      `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.value`,
                    ),
                  )
            }
            sx={styles.inputField}
            disabled={!isEdit || type === 'Percentage'}
            onChange={() => {
              formMethods.setValue(
                `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                chargeException?.id,
              )
              formMethods.setValue(
                `vehicle.waitAndReturn.chargingPeriod.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                type,
              )
            }}
          />
        </Grid>
      )}
      {type && additionalStop && (
        <Grid item xs={1}>
          <CurrencyInput
            label=""
            name={`vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.value`}
            defaultValue={
              type === 'Percentage'
                ? calcValue(
                    formMethods.getValues('vehicle.additionalStop.price'),
                    percentage,
                  )
                : calcValue(
                    formMethods.getValues(
                      `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.value`,
                    ),
                  )
            }
            sx={styles.inputField}
            disabled={!isEdit || type === 'Percentage'}
            onChange={() => {
              formMethods.setValue(
                `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                chargeException?.id,
              )
              formMethods.setValue(
                `vehicle.additionalStop.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                type,
              )
            }}
          />
        </Grid>
      )}
      {order.map((i) => {
        const rate = chargeRates[i]
        if (!rate) return null
        const getFixedPrice = () => {
          const isOverrideApplied =
            chargeRateException?.chargeExceptionId === chargeException.id

          if (!isOverrideApplied) {
            return
          }

          if (type === 'Percentage') {
            return calcValue(
              formMethods.getValues(`chargeRates.${i}.pricePerMile`),
              percentage,
            )
          }
          return (
            chargeRateException && Number(chargeRateException?.value).toFixed(2)
          )
        }
        return (
          <Fragment key={`${rate.id}_${i}`}>
            {type && (
              <>
                <Grid item key={i} xs={1}>
                  {isEdit && type === 'Percentage' && (
                    <FormInput
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">£</InputAdornment>
                        ),
                      }}
                      name={`chargeExceptions.${chargeExceptionIndex}.value`}
                      type="tel"
                      sx={styles.inputField}
                      value={
                        rate.chargeExceptions[chargeExceptionIndex]?.value
                          ? Number(
                              rate.chargeExceptions[chargeExceptionIndex]
                                ?.value,
                            ).toFixed(2)
                          : Number(
                              formMethods.getValues(
                                `chargeRates.${i}.pricePerMile`,
                              ),
                            ).toFixed(2)
                      }
                      disabled={true}
                      onChange={() => {
                        formMethods.setValue(
                          `chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                          chargeException.id,
                        )
                        formMethods.setValue(
                          `chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                          type,
                        )
                        if (type === 'Percentage') {
                          formMethods.setValue(
                            `chargeExceptions.${chargeExceptionIndex}.value`,
                            percentage,
                          )
                          formMethods.setValue(
                            `chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.value`,
                            percentage,
                          )
                        }
                      }}
                    />
                  )}
                  {isEdit && type === 'Fixed' && (
                    <FormInput
                      name={`chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.value`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">£</InputAdornment>
                        ),
                      }}
                      type="tel"
                      sx={styles.inputField}
                      onChange={() => {
                        formMethods.setValue(
                          `chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.chargeExceptionId`,
                          chargeException.id,
                        )
                        formMethods.setValue(
                          `chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.chargeTypeName`,
                          type,
                        )
                      }}
                    />
                  )}
                  {!isEdit && (
                    <FormInput
                      name={`chargeRates.${i}.chargeExceptions.${chargeExceptionIndex}.value`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">£</InputAdornment>
                        ),
                      }}
                      type="tel"
                      sx={styles.inputField}
                      defaultValue={getFixedPrice()}
                      disabled={!isEdit}
                    />
                  )}
                </Grid>
              </>
            )}
          </Fragment>
        )
      })}
    </Grid>
  )
}
interface ChargeOverridesProps {
  waitingTime: string
  chargeRates: Array<ChargeRate>
  setChargeRates: Dispatch<React.SetStateAction<Array<ChargeRate>>>
  isEdit: boolean
  additionalStop: boolean
  waitAndReturn: boolean
  displayOrder?: number[]
}
export const ChargeOverrides = (props: ChargeOverridesProps) => {
  const {
    waitingTime,
    chargeRates,
    setChargeRates,
    isEdit,
    additionalStop,
    waitAndReturn,
    displayOrder,
  } = props
  const { chargeExceptions } = useSupplierState()

  return (
    <>
      {chargeExceptions.map((item: ChargeException, index: number) => (
        <ExceptionRow
          key={`${item.name}_${item.id}_${index}`}
          chargeExceptionIndex={index}
          chargeException={item}
          waitingTime={waitingTime}
          additionalStop={additionalStop}
          waitAndReturn={waitAndReturn}
          chargeRates={chargeRates}
          setChargeRates={setChargeRates}
          isEdit={!!isEdit}
          displayOrder={displayOrder}
        />
      ))}
    </>
  )
}

export default ChargeOverrides
